import {
  AutoComplete,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import React, { useId } from "react";
import "./SharedAutoComplete.css";

interface SharedAutoCompleteProps {
  value: string;
  suggestions: any[];
  completeMethod: (event: AutoCompleteCompleteEvent) => void;
  field: string;
  placeholder?: string;
  onChange: (e: any) => void;
  onSelect?: (e: any) => void;
  disabled?: boolean;
  className?: string;
  dropdown?: boolean;
  error?: string;
  panelClassName?: string;
  /** Unique ID for the component. If not provided, a unique ID will be generated automatically. */
  id?: string;
}

/**
 * Shared AutoComplete component with consistent styling and appendTo="self" behavior
 * to ensure dropdowns display correctly within modals, drawers, and scrollable containers.
 *
 * Features:
 * - Automatic appendTo="self" for proper positioning in modals/drawers
 * - Unique portal IDs to prevent conflicts between multiple autocomplete instances
 * - Consistent styling with Bootstrap form-control
 * - Built-in error state handling
 * - Dropdown button support
 *
 * @example
 * ```tsx
 * <SharedAutoComplete
 *   value={inputText}
 *   suggestions={filteredItems}
 *   completeMethod={handleSearch}
 *   field="label"
 *   placeholder="Select an option"
 *   onChange={handleChange}
 *   onSelect={handleSelect}
 *   error={errors.fieldName}
 *   id="country-autocomplete"
 * />
 * ```
 */
const SharedAutoComplete: React.FC<SharedAutoCompleteProps> = ({
  value,
  suggestions,
  completeMethod,
  field,
  placeholder = "Type to search...",
  onChange,
  onSelect,
  disabled = false,
  className = "",
  dropdown = true,
  error,
  panelClassName = "shared-autocomplete-panel",
  id,
}) => {
  // Generate a unique ID if not provided
  const autoId = useId();
  const uniqueId = id || autoId;
  const uniquePanelClassName = `${panelClassName}-${uniqueId.replace(
    /:/g,
    "-"
  )}`;

  return (
    <AutoComplete
      id={uniqueId}
      value={value}
      suggestions={suggestions}
      completeMethod={completeMethod}
      field={field}
      placeholder={placeholder}
      onChange={onChange}
      onSelect={onSelect}
      disabled={disabled}
      dropdown={dropdown}
      className={`w-100 ${error ? "p-invalid" : ""} ${className}`}
      inputClassName="form-control p-autocomplete-input"
      panelClassName={uniquePanelClassName}
      appendTo="self"
    />
  );
};

export default SharedAutoComplete;
